import { getAllUnverifiedBrands, queryKeys } from '@vgrubs/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { BrandRequestEntity } from 'components/src/types/brand.types';

const PRELOAD_ROOT_MARGIN = '1000px';

const fetchUnverifiedBrands = async ({
  pageParam = 1,
  query,
}: {
  pageParam?: number;
  query: string;
}) => {
  const { data } = await getAllUnverifiedBrands({
    limit: 20,
    page: pageParam,
    query: query || undefined,
  });

  const brandsVerifications: BrandRequestEntity[] = data?.data ?? [];
  const nextPage =
    brandsVerifications.length === 20 ? pageParam + 1 : undefined;

  return {
    brandsVerifications,
    nextPage,
  };
};

export const useVerificationsBrandsInfiniteScroll = (query: string) => {
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: [...queryKeys.admin.verifications.brands, query],
    queryFn: ({ pageParam = 1 }) => fetchUnverifiedBrands({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const brandsVerifications: BrandRequestEntity[] =
    data?.pages.flatMap((page) => page.brandsVerifications) ?? [];

  return {
    brandsVerifications,
    observerRef,
    isFetchingNextPage,
    status,
    refetch,
  };
};
